import { useState, useEffect, useCallback } from 'react';
import { Lightbulb, Send, Loader2, Trash2, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

const STATUS = {
  nova:         { label: 'Nova',         classe: 'bg-blue-100 text-blue-700' },
  em_analise:   { label: 'Em análise',   classe: 'bg-amber-100 text-amber-700' },
  aprovada:     { label: 'Aprovada',     classe: 'bg-green-100 text-green-700' },
  implementada: { label: 'Implementada', classe: 'bg-emerald-600 text-white' },
  descartada:   { label: 'Descartada',   classe: 'bg-gray-200 text-gray-600' },
};

function formatarDataHora(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const erroApi = (err, padrao) => err?.response?.data?.error || padrao;

function FormNovaIdeia({ onCriada }) {
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [anonimo, setAnonimo] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const handleEnviar = async (e) => {
    e.preventDefault();
    if (!titulo.trim() || !descricao.trim()) {
      toast.error('Preencha o título e a descrição.');
      return;
    }
    setEnviando(true);
    try {
      await apiService.ideias.criar({ titulo: titulo.trim(), descricao: descricao.trim(), anonimo });
      toast.success('Ideia enviada! Obrigado pela contribuição 💡');
      setTitulo('');
      setDescricao('');
      setAnonimo(false);
      onCriada();
    } catch (err) {
      toast.error(erroApi(err, 'Erro ao enviar ideia.'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form onSubmit={handleEnviar} className="bg-white rounded-lg shadow p-5 space-y-3">
      <h2 className="font-semibold text-gray-800">Nova ideia</h2>
      <input
        type="text"
        value={titulo}
        onChange={e => setTitulo(e.target.value)}
        maxLength={150}
        placeholder="Título (ex.: Organizar vitrine por faixa de preço)"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none"
      />
      <textarea
        value={descricao}
        onChange={e => setDescricao(e.target.value)}
        maxLength={5000}
        rows={4}
        placeholder="Descreva a ideia: qual problema resolve e como funcionaria"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none"
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={anonimo} onChange={e => setAnonimo(e.target.checked)} />
          Enviar anonimamente (seu nome não aparece para ninguém)
        </label>
        <button
          type="submit"
          disabled={enviando}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-amber-600 disabled:opacity-50"
        >
          {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Enviar
        </button>
      </div>
    </form>
  );
}

function PainelAvaliacao({ ideia, onSalva }) {
  const [status, setStatus] = useState(ideia.status);
  const [resposta, setResposta] = useState(ideia.resposta || '');
  const [salvando, setSalvando] = useState(false);

  const handleSalvar = async () => {
    setSalvando(true);
    try {
      await apiService.ideias.avaliar(ideia.id, { status, resposta });
      toast.success('Avaliação salva.');
      onSalva();
    } catch (err) {
      toast.error(erroApi(err, 'Erro ao salvar avaliação.'));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-500 uppercase">Avaliação</span>
        <select
          value={status}
          onChange={e => setStatus(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
        >
          {Object.entries(STATUS).map(([valor, { label }]) => (
            <option key={valor} value={valor}>{label}</option>
          ))}
        </select>
      </div>
      <textarea
        value={resposta}
        onChange={e => setResposta(e.target.value)}
        maxLength={5000}
        rows={2}
        placeholder="Resposta para o colaborador (opcional)"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
      />
      <div className="flex justify-end">
        <button
          onClick={handleSalvar}
          disabled={salvando}
          className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-indigo-700 disabled:opacity-50"
        >
          {salvando && <Loader2 className="w-4 h-4 animate-spin" />}
          Salvar avaliação
        </button>
      </div>
    </div>
  );
}

function CardIdeia({ ideia, gestor, onAtualizar }) {
  const status = STATUS[ideia.status] || STATUS.nova;
  const podeExcluir = ideia.minha && ideia.status === 'nova';

  const handleExcluir = async () => {
    if (!window.confirm('Excluir esta ideia?')) return;
    try {
      await apiService.ideias.excluir(ideia.id);
      toast.success('Ideia excluída.');
      onAtualizar();
    } catch (err) {
      toast.error(erroApi(err, 'Erro ao excluir ideia.'));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-gray-800 break-words">{ideia.titulo}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {ideia.autor}{ideia.minha && ideia.anonimo ? ' (você)' : ''} · {formatarDataHora(ideia.criado_em)}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.classe}`}>{status.label}</span>
          {podeExcluir && (
            <button onClick={handleExcluir} className="p-1 text-gray-400 hover:text-red-600" title="Excluir">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap break-words">{ideia.descricao}</p>

      {ideia.resposta && (
        <div className="mt-3 bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-sm">
          <div className="flex items-center gap-1 text-xs font-medium text-indigo-700 mb-1">
            <MessageSquare className="w-3.5 h-3.5" />
            Resposta de {ideia.respondido_por || 'gestão'} · {formatarDataHora(ideia.respondido_em)}
          </div>
          <p className="text-gray-700 whitespace-pre-wrap break-words">{ideia.resposta}</p>
        </div>
      )}

      {gestor && <PainelAvaliacao key={`${ideia.id}-${ideia.status}`} ideia={ideia} onSalva={onAtualizar} />}
    </div>
  );
}

export default function CaixaIdeias() {
  const { isGerenteOuAdmin } = useAuth();
  const gestor = isGerenteOuAdmin();
  const [ideias, setIdeias] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiService.ideias.listar(filtroStatus ? { status: filtroStatus } : {});
      setIdeias(resp.data.data ?? []);
    } catch {
      toast.error('Erro ao carregar ideias.');
    } finally {
      setLoading(false);
    }
  }, [filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-lg shadow-lg p-6 text-white flex items-center gap-4">
        <Lightbulb className="w-10 h-10 shrink-0" />
        <div>
          <h1 className="text-2xl font-bold">Caixa de Ideias</h1>
          <p className="text-amber-50 text-sm mt-1">
            Tem uma sugestão para melhorar a loja, o atendimento ou o sistema? Registre aqui.
          </p>
        </div>
      </div>

      <FormNovaIdeia onCriada={carregar} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-800">{gestor ? 'Ideias recebidas' : 'Minhas ideias'}</h2>
        <select
          value={filtroStatus}
          onChange={e => setFiltroStatus(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
        >
          <option value="">Todos os status</option>
          {Object.entries(STATUS).map(([valor, { label }]) => (
            <option key={valor} value={valor}>{label}</option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="text-center text-gray-400 py-8">
          <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Carregando...
        </div>
      )}
      {!loading && ideias.length === 0 && (
        <div className="text-center text-gray-400 py-8">Nenhuma ideia encontrada.</div>
      )}
      {!loading && (
        <div className="space-y-4">
          {ideias.map(ideia => (
            <CardIdeia key={ideia.id} ideia={ideia} gestor={gestor} onAtualizar={carregar} />
          ))}
        </div>
      )}
    </div>
  );
}
